/**
 * ReportingNowBadge — small circular "R" shown in the narrow live-report-status
 * column when a study is currently open in the report editor by some user
 * (order_workflow.ow_reporting_status === 'R'). The study is locked while this
 * is set, so it doubles as an "in use" indicator.
 *
 * Replaces the bare "R" text with a styled, tooltip-backed badge. Renders
 * nothing when no one has the study open, keeping the column empty as before.
 *
 * The hover tooltip identifies whoever currently holds the study as
 * "<name> (<username>)". When only one of the two is known it shows just that;
 * when neither is available it falls back to the plain "Currently being reported".
 */
import React from 'react';
import { Tooltip } from 'antd';

export const ReportingNowBadge: React.FC<{
  status?: string | null;
  reporterName?: string | null;
  reporterUsername?: string | null;
}> = ({ status, reporterName, reporterUsername }) => {
  if (status !== 'R') return null;

  const name = (reporterName || '').trim();
  const username = (reporterUsername || '').trim();
  const tooltipTitle =
    name && username
      ? `${name} (${username})`
      : name || username || 'Currently being reported';

  return (
    <Tooltip title={tooltipTitle}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          // Glossy 3D violet orb: a top-lit radial gradient for the "bump", an
          // inset white highlight along the top + inset dark edge at the bottom
          // for depth, and a soft drop shadow lifting it off the row.
          background:
            'radial-gradient(circle at 50% 30%, #B488F9 0%, #7C3AED 55%, #5B21B6 100%)',
          boxShadow:
            '0 1px 2px rgba(76, 29, 149, 0.45), inset 0 1px 1.5px rgba(255, 255, 255, 0.6), inset 0 -1.5px 2px rgba(76, 29, 149, 0.55)',
          color: '#fff',
          textShadow: '0 1px 1px rgba(0, 0, 0, 0.3)',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: '11px',
          lineHeight: '20px',
          letterSpacing: 0,
        }}
      >
        R
      </span>
    </Tooltip>
  );
};

export default ReportingNowBadge;
